export const BYTES_PER_MEGABYTE = 1024 * 1024;
export const MAX_CANDIDATE_DOCUMENTS = 20;
export const MAX_GENERATION_DOCUMENTS = 10;
export const MAX_CLIENT_GENERATION_DOCUMENTS = MAX_GENERATION_DOCUMENTS;
const DOCUMENT_MAX_SIZE_MB = 10;

export type UploadRule = {
  accept: string;
  allowedExtensions: readonly string[];
  allowedMimeTypes: readonly string[];
  maxSizeBytes: number;
  typeError: string;
  sizeError: string;
  description: string;
};
export type RejectedSelectedFile = {
  file: File;
  reason: string;
};

export const UPLOAD_MESSAGES = {
  emptySelection: "No file selected",
  required: "A file is required!",
  failed: "Failed to upload or process file.",
  success: "File processed successfully!",
} as const;

export const DOCUMENT_UPLOAD_RULE: UploadRule = {
  accept: ".pdf,.docx,.pptx,.xlsx,.txt,.png,.jpg,.jpeg,.webp,.bmp",
  allowedExtensions: [
    ".pdf",
    ".docx",
    ".pptx",
    ".xlsx",
    ".txt",
    ".png",
    ".jpg",
    ".jpeg",
    ".webp",
    ".bmp",
  ],
  allowedMimeTypes: [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/bmp",
  ],
  maxSizeBytes: DOCUMENT_MAX_SIZE_MB * BYTES_PER_MEGABYTE,
  typeError: "Unsupported document type!",
  sizeError: `File size must be less than ${DOCUMENT_MAX_SIZE_MB}MB!`,
  description: `Supported files: PDF, DOCX, PPTX, XLSX, TXT, images. ${DOCUMENT_MAX_SIZE_MB}MB max per file.`,
};

const fileHasAllowedExtension = (file: File, rule: UploadRule) => {
  const fileName = (file.name || "").toLowerCase();
  if (!fileName) return false;
  return rule.allowedExtensions.some((extension) =>
    fileName.endsWith(extension),
  );
};
const INCONCLUSIVE_BROWSER_MIME_TYPES = new Set([
  "application/octet-stream",
  "application/zip",
  "application/x-zip-compressed",
]);

const BROWSER_MIME_TYPES_BY_EXTENSION: Readonly<
  Record<string, readonly string[]>
> = {
  ".pdf": ["application/pdf"],
  ".docx": [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ],
  ".pptx": [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.ms-powerpoint",
  ],
  ".xlsx": [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ],
  ".txt": ["text/plain", "application/txt"],
  ".png": ["image/png"],
  ".jpg": ["image/jpeg", "image/jpg", "image/pjpeg"],
  ".jpeg": ["image/jpeg", "image/jpg", "image/pjpeg"],
  ".webp": ["image/webp"],
  ".bmp": ["image/bmp", "image/x-bmp", "image/x-ms-bmp"],
};

export const getDocumentUploadMimeType = (file: File) =>
  BROWSER_MIME_TYPES_BY_EXTENSION[getFileExtension(file)]?.[0] || file.type;

const getFileExtension = (file: File) => {
  const extensionStart = file.name.lastIndexOf(".");
  return extensionStart < 0
    ? ""
    : file.name.slice(extensionStart).toLowerCase();
};

export const fileMatchesUploadRule = (file: File, rule: UploadRule) => {
  const mimeType = file.type?.split(";", 1)[0].trim().toLowerCase();
  const extension = getFileExtension(file);
  const extensionMimeTypes = BROWSER_MIME_TYPES_BY_EXTENSION[extension];
  const hasAllowedMimeType =
    !mimeType ||
    INCONCLUSIVE_BROWSER_MIME_TYPES.has(mimeType) ||
    (extensionMimeTypes
      ? extensionMimeTypes.includes(mimeType)
      : rule.allowedMimeTypes.includes(mimeType));
  return fileHasAllowedExtension(file, rule) && hasAllowedMimeType;
};

export const partitionFilesByUploadRule = (
  files: readonly File[],
  rule: UploadRule,
): { acceptedFiles: File[]; rejectedFiles: RejectedSelectedFile[] } => {
  const acceptedFiles: File[] = [];
  const rejectedFiles: RejectedSelectedFile[] = [];

  files.forEach((file) => {
    if (!fileMatchesUploadRule(file, rule)) {
      rejectedFiles.push({
        file,
        reason: `File "${file.name}" is not a supported format.`,
      });
      return;
    }

    if (file.size > rule.maxSizeBytes) {
      rejectedFiles.push({
        file,
        reason: `File "${file.name}" is too large. ${rule.sizeError}`,
      });
      return;
    }

    acceptedFiles.push(file);
  });

  return { acceptedFiles, rejectedFiles };
};

export const formatRejectedSelectedFiles = (
  rejectedFiles: readonly RejectedSelectedFile[],
) => rejectedFiles.map(({ reason }) => reason).join(" ");
